import type { OperatorProfile, ProfileViewMode, RealProfile } from '../types/profiles';

export interface DateRange {
  startMs: number;
  endMs: number;
}

export interface AgeRange {
  min?: number;
  max?: number;
}

function dateRangeFromEvent(startDate: string, endDate: string): DateRange | null {
  if (!startDate) return null;
  const startMs = new Date(startDate).getTime();
  // endDate: inclui o dia inteiro (23:59:59)
  const endMs = endDate
    ? new Date(endDate + 'T23:59:59').getTime()
    : new Date(startDate + 'T23:59:59').getTime();
  return { startMs, endMs };
}

export function eventDateRange(
  noPeriod: boolean,
  startDate: string,
  endDate: string
): DateRange | null {
  if (noPeriod || !startDate) return null;
  return dateRangeFromEvent(startDate, endDate);
}

/** Filtra subcoleções que possuem timestamp próprio. */
function applyDateToSubcollections(profile: RealProfile, range: DateRange): RealProfile {
  const { startMs, endMs } = range;
  const inRange = (ts?: number | null): boolean =>
    ts == null || (ts >= startMs && ts <= endMs);

  return {
    ...profile,
    categories: profile.categories.filter((c) => inRange(c.createdAt)),
    subcategories: profile.subcategories.filter((s) => inRange(s.createdAt)),
    subRoutines: profile.subRoutines.filter((sr) => inRange(sr.createdAt)),
    drawingMetrics: profile.drawingMetrics.filter((d) => inRange(d.createdAt)),
    gameMetrics: profile.gameMetrics.filter((g) => inRange(g.firstUseAt)),
    // subcategoryClicks, routines, gameRoundMetrics, musicPlays, genrePlays,
    // cardClicks, progressStats não têm timestamp de criação individual —
    // incluídos integralmente
    subcategoryClicks: profile.subcategoryClicks,
    routines: profile.routines,
    gameRoundMetrics: profile.gameRoundMetrics,
    musicPlays: profile.musicPlays,
    genrePlays: profile.genrePlays,
    cardClicks: profile.cardClicks,
    progressStats: profile.progressStats,
  };
}

/**
 * Filtra perfis (qualquer `kind`) só pelo intervalo de data do evento — sem
 * separar managed/individual. Usado pros totais gerais do evento (participantes/
 * registros do card de topo), que devem contar todo mundo que apareceu no
 * período, não só a visão "Alunos" selecionada no filtro do dashboard (ver
 * pedido do usuário: antes da separação profile/student, professor e aluno
 * contavam igual nos totais do site).
 *
 * `createdAt` OU `updatedAt` no intervalo — nunca só `createdAt` sozinho: um
 * perfil criado bem antes do evento (ex.: conta legada de `users/{userId}`
 * que já existia há meses) mas com atividade nova sincronizada DURANTE o
 * período do evento tinha `updatedAt` dentro do intervalo e `createdAt` fora,
 * e ficava de fora do evento inteiro por causa disso — mesmo tendo dado de
 * verdade naquela data (achado do usuário: evento datado 22/09 não mostrava
 * nenhum registro de quem só voltou a usar o app naquele dia).
 */
export function filterProfilesByDate(
  profiles: RealProfile[],
  dateRange?: DateRange | null
): RealProfile[] {
  if (!dateRange) return profiles;
  const { startMs, endMs } = dateRange;
  const inRange = (ts: number) => ts >= startMs && ts <= endMs;
  const result = profiles.filter((p) => inRange(p.createdAt) || inRange(p.updatedAt));
  return result.map((p) => applyDateToSubcollections(p, dateRange));
}

/** Indexa operadores por roomId — `OperatorProfile.uid` é, na prática, o id de
 *  `profiles/{authUid}/rooms/{roomId}` (ver conectar.md, adendo A13), o mesmo
 *  id que `RealProfile.writerRoomIds` referencia. */
export function indexOperatorsByRoomId(operators: OperatorProfile[]): Map<string, OperatorProfile> {
  return new Map(operators.map((o) => [o.uid, o]));
}

const PARENT_ROLES = new Set(['pai', 'mae']);
const STUDENT_ROLES = new Set(['filho', 'filha']);

/** Papel (`profileRole`) de quem opera um perfil `kind: 'individual'`. Perfis
 *  `source: 'legacy'` já trazem isso direto no próprio doc (`users/{userId}.
 *  profileRole` — schema antigo não tem o conceito de operador/room separado).
 *  Perfis `source: 'new'` resolvem indiretamente pelo primeiro `writerRoomId`
 *  com operador conhecido (uso individual normalmente só tem 1 escritor: a
 *  própria pessoa). `undefined` quando não dá pra resolver (operador ainda
 *  não sincronizado, papel não gravado, ou perfil `managed`). */
export function individualProfileRole(
  profile: RealProfile,
  operatorsByRoomId: Map<string, OperatorProfile>
): string | undefined {
  if (profile.profileRole) return profile.profileRole;
  // Cache do Redis pode ter buckets gravados antes do campo `writerRoomIds`
  // existir (ver api/profiles/sync.ts) — só docs tocados num sync incremental
  // ganham o campo; um doc parado desde antes disso fica sem ele indefinidamente.
  for (const roomId of profile.writerRoomIds ?? []) {
    const role = operatorsByRoomId.get(roomId)?.profileRole;
    if (role) return role;
  }
  return undefined;
}

export interface ContentManagementStats {
  categories: number;
  subcategories: number;
  routines: number;
  subRoutines: number;
}

/**
 * Quantas categorias/subcategorias/rotinas/subrotinas um professor ou pai/mãe
 * (perfil `kind: 'individual'`) tem sob sua responsabilidade — métrica
 * alternativa às atividades normais (jogos, CAA, música...) pra esse papel,
 * que no schema novo não tem atividade própria de verdade (ver comentário de
 * `RealProfile.source` em types/profiles.ts).
 *
 * `source: 'legacy'`: conta direto do próprio perfil — no schema antigo cada
 * usuário registrava sua própria atividade, currículo incluso.
 *
 * `source: 'new'`: o professor/pai não grava currículo no PRÓPRIO perfil —
 * quem recebe categorias/rotinas é o aluno gerenciado. Agrega então o
 * currículo de todo aluno (`kind: 'managed'`) onde esse professor/pai (via
 * `writerRoomIds`) é um dos escritores atuais. Não dá pra atribuir por item
 * quem exatamente criou cada categoria quando há mais de um escritor no mesmo
 * aluno — o app não grava isso (ver `ActivityEntry` em types/profiles.ts) —
 * então é "conteúdo sob a responsabilidade de", não "criado exclusivamente por".
 */
export function contentManagementStatsFor(
  profile: RealProfile,
  allProfiles: RealProfile[]
): ContentManagementStats {
  if (profile.source === 'legacy') {
    return {
      categories: profile.categories.length,
      subcategories: profile.subcategories.length,
      routines: profile.routines.length,
      subRoutines: profile.subRoutines.length,
    };
  }

  const roomIds = new Set(profile.writerRoomIds ?? []);
  const managedStudents =
    roomIds.size === 0
      ? []
      : allProfiles.filter((p) => p.kind === 'managed' && p.writerRoomIds?.some((id) => roomIds.has(id)));

  return managedStudents.reduce(
    (acc, s) => ({
      categories: acc.categories + s.categories.length,
      subcategories: acc.subcategories + s.subcategories.length,
      routines: acc.routines + s.routines.length,
      subRoutines: acc.subRoutines + s.subRoutines.length,
    }),
    { categories: 0, subcategories: 0, routines: 0, subRoutines: 0 }
  );
}

/** De categorias/subcategorias/rotinas/subrotinas, qual tipo um professor/pai
 *  criou mais — mesma ideia de `topFeatureForProfile` (realGeneralAnalytics.ts),
 *  só que pro lado "o que ele criou" em vez de "o que ele usou". `null` quando
 *  não tem nada. */
export function topCreatedContentType(stats: ContentManagementStats): { name: string; value: number } | null {
  const candidates = [
    { name: 'Categorias', value: stats.categories },
    { name: 'Subcategorias', value: stats.subcategories },
    { name: 'Rotinas', value: stats.routines },
    { name: 'Subrotinas', value: stats.subRoutines },
  ];
  const top = candidates.reduce((best, c) => (c.value > best.value ? c : best), candidates[0]);
  return top.value > 0 ? top : null;
}

/**
 * Um perfil pertence a uma visão (`ProfileViewMode`) quando: "todos" bate com
 * qualquer perfil (agregado, sem distinguir papel — visão padrão do filtro).
 * "managed" bate direto com `kind` — e também com perfis `kind: 'individual'`
 * cujo papel resolvido é "filho"/"filha" (aluno autônomo é a mesma categoria
 * de aluno(a) no site, pedido do usuário). "professor"/"pai_mae" exigem
 * `kind: 'individual'` **e** o `profileRole` do operador resolvido bater com
 * a visão — um perfil individual sem papel conhecido (operador ainda não
 * sincronizado, ou conta antiga sem o campo) não aparece em nenhuma das
 * visões específicas, em vez de cair silenciosamente num balde errado.
 */
export function matchesViewMode(
  profile: RealProfile,
  mode: ProfileViewMode,
  operatorsByRoomId: Map<string, OperatorProfile>
): boolean {
  if (mode === 'todos') return true;
  if (profile.kind === 'managed') return mode === 'managed';
  const role = individualProfileRole(profile, operatorsByRoomId);
  if (STUDENT_ROLES.has(role ?? '')) return mode === 'managed';
  if (mode === 'managed') return false;
  return mode === 'professor' ? role === 'professor' : PARENT_ROLES.has(role ?? '');
}

/**
 * Filtra os alunos (`RealProfile[]`) por visão (ver todos / aluno / professor /
 * pai-mãe) — "todos" agrega; as outras 3 nunca se misturam entre si no mesmo
 * resultado (ver conectar.md, tarefa S4). Usado pelos módulos de análise
 * detalhada (jogos, comunicação, etc.), não pelos totais gerais do evento —
 * ver `filterProfilesByDate` pra isso.
 */
export function filterProfiles(
  profiles: RealProfile[],
  mode: ProfileViewMode,
  opts?: {
    individualId?: string;
    dateRange?: DateRange | null;
    ageRange?: AgeRange | null;
    /** "masculino" | "feminino" — vazio/undefined não filtra. Aplicado nas 3 visões
     *  (não só "managed"), já que `sex` existe no mesmo doc `students/{code}`
     *  independente do `kind`. */
    sex?: string;
    operators?: OperatorProfile[];
  }
): RealProfile[] {
  const { individualId, dateRange, ageRange, sex, operators } = opts ?? {};
  const operatorsByRoomId = indexOperatorsByRoomId(operators ?? []);

  // 1. Separação por visão — nunca mistura as 3.
  let result = profiles.filter((p) => matchesViewMode(p, mode, operatorsByRoomId));

  // 2. Filtro de data: inclui perfis cujo createdAt está no intervalo do evento.
  result = filterProfilesByDate(result, dateRange);

  // 2b. Filtro de gênero: aplica nas 3 visões.
  if (sex) {
    result = result.filter((p) => p.sex === sex);
  }

  // 3. Visões de uso individual (professor/pai_mae): sempre um participante por vez.
  if (mode === 'professor' || mode === 'pai_mae') {
    if (individualId) result = result.filter((p) => p.id === individualId);
    return result;
  }

  // 4. Visões agregadas ("todos"/"managed"): filtro por idade, disponível.
  if (ageRange && (ageRange.min != null || ageRange.max != null)) {
    result = result.filter((p) => {
      const age = p.age;
      if (age == null) return true; // sem idade → inclui (não penaliza quem não cadastrou)
      if (ageRange.min != null && age < ageRange.min) return false;
      if (ageRange.max != null && age > ageRange.max) return false;
      return true;
    });
  }

  return result;
}

/** Rótulo anônimo aleatório por sessão — não persiste entre recarregamentos. */
export function buildAnonymousLabels(profiles: RealProfile[]): Map<string, string> {
  const shuffled = [...profiles].sort(() => Math.random() - 0.5);
  const labels = new Map<string, string>();
  shuffled.forEach((p, idx) => labels.set(p.id, `Participante ${idx + 1}`));
  return labels;
}
